import { supabase } from '../lib/supabase';

// ── Notification type config ────────────────────────────────────────
// Maps each notification type to its display properties and preference column.
const NOTIFICATION_CONFIG = {
  AssignmentCreated: {
    title: (ctx) => `New assignment: ${ctx.jobTitle || 'Untitled'}`,
    body: (ctx) => ctx.rate ? `Rate: $${ctx.rate}/hr` : null,
    link: (ctx) => ctx.assignmentId ? `/jobs?tab=myjobs&assignment=${ctx.assignmentId}` : '/jobs?tab=myjobs',
    icon: '📋',
    pref: 'notif_assignments',
  },
  ApplicationAccepted: {
    title: (ctx) => `Application accepted: ${ctx.jobTitle || 'Untitled'}`,
    body: () => 'Your application has been approved. Check your assignments.',
    link: (ctx) => ctx.jobId ? `/jobs?tab=applications&job=${ctx.jobId}` : '/jobs?tab=applications',
    icon: '✅',
    pref: 'notif_applications',
  },
  ApplicationRejected: {
    title: (ctx) => `Application update: ${ctx.jobTitle || 'Untitled'}`,
    body: () => 'Your application was not selected for this project.',
    link: (ctx) => ctx.jobId ? `/jobs?tab=applications&job=${ctx.jobId}` : '/jobs?tab=applications',
    icon: '📝',
    pref: 'notif_applications',
  },
  OnboardingComplete: {
    title: (ctx) => `Onboarding complete: ${ctx.jobTitle || 'Untitled'}`,
    body: () => 'You are now active on this project.',
    link: (ctx) => ctx.assignmentId ? `/jobs?tab=myjobs&assignment=${ctx.assignmentId}` : '/jobs?tab=myjobs',
    icon: '🎉',
    pref: 'notif_assignments',
  },
  BatchAssigned: {
    title: (ctx) => `New batch assigned: ${ctx.jobTitle || 'Untitled'}`,
    body: (ctx) => ctx.itemCount ? `${ctx.itemCount} items to annotate` : null,
    link: (ctx) => ctx.jobId ? `/jobs?tab=myjobs&job=${ctx.jobId}` : '/jobs?tab=myjobs',
    icon: '📦',
    pref: 'notif_batches',
  },
  RevisionRequested: {
    title: (ctx) => `Revision requested: ${ctx.jobTitle || 'Untitled'}`,
    body: () => 'Some items need your attention.',
    link: (ctx) => ctx.jobId ? `/jobs?tab=myjobs&job=${ctx.jobId}` : '/jobs?tab=myjobs',
    icon: '✏️',
    pref: 'notif_batches',
  },
  BatchAccepted: {
    title: (ctx) => `Batch accepted: ${ctx.jobTitle || 'Untitled'}`,
    body: () => 'Your batch passed Quality review.',
    link: (ctx) => ctx.jobId ? `/jobs?tab=myjobs&job=${ctx.jobId}` : '/jobs?tab=myjobs',
    icon: '🏆',
    pref: 'notif_batches',
  },
  PaymentProcessed: {
    title: (ctx) => `Payment processed${ctx.amount ? `: $${ctx.amount}` : ''}`,
    body: () => 'Check your payments tab for details.',
    link: () => '/professional',
    icon: '💰',
    pref: 'notif_payments',
  },
  // ── Client notification types ───────────────────────────────
  ClientDeliverableReady: {
    title: (ctx) => `Batch${ctx.batchNumber ? ` #${ctx.batchNumber}` : ''} ready for review: ${ctx.jobTitle || 'Untitled'}`,
    body: () => 'A new deliverable is ready for your review.',
    link: (ctx) => ctx.jobId ? `/client/project/${ctx.jobId}` : '/client',
    icon: '📬',
    pref: 'notif_client_updates',
  },
  ClientRevisionCompleted: {
    title: (ctx) => `Revision resubmitted: ${ctx.jobTitle || 'Untitled'}`,
    body: () => 'The team has completed revisions and resubmitted for your review.',
    link: (ctx) => ctx.jobId ? `/client/project/${ctx.jobId}` : '/client',
    icon: '🔄',
    pref: 'notif_client_updates',
  },
  ClientProjectCompleted: {
    title: (ctx) => `Project completed: ${ctx.jobTitle || 'Untitled'}`,
    body: () => 'All deliverables have been completed for this project.',
    link: (ctx) => ctx.jobId ? `/client/project/${ctx.jobId}` : '/client',
    icon: '🏁',
    pref: 'notif_client_updates',
  },
  ClientRequestSubmitted: {
    title: (ctx) => `Client request: ${ctx.jobTitle || 'Untitled'}`,
    body: (ctx) => ctx.requestType ? `Type: ${ctx.requestType.replace(/_/g, ' ')}` : 'New client request submitted.',
    link: (ctx) => ctx.jobId ? `/admin/project/${ctx.jobId}` : '/admin',
    icon: '📋',
    pref: null, // Always deliver to SPL/admin — no preference opt-out for operational alerts
  },
  ClientRequestResolved: {
    title: (ctx) => `Request ${ctx.status || 'updated'}: ${ctx.requestTitle || ctx.jobTitle || 'Untitled'}`,
    body: (ctx) => `Your request on "${ctx.jobTitle || 'project'}" has been ${ctx.status || 'responded to'}.`,
    link: (ctx) => ctx.jobId ? `/client/project/${ctx.jobId}` : '/client',
    icon: '✅',
    pref: null,
  },
  ClientReportReady: {
    title: (ctx) => `${(ctx.reportType || 'report').charAt(0).toUpperCase() + (ctx.reportType || 'report').slice(1)} report ready: ${ctx.jobTitle || 'Untitled'}`,
    body: () => 'A new report is available for download in your portal.',
    link: (ctx) => ctx.jobId ? `/client/project/${ctx.jobId}` : '/client',
    icon: '📊',
    pref: 'notif_client_updates',
  },
};

async function resolveRecipientContext(userId, prefColumn) {
  if (!userId) return null;

  const columns = ['org_id'];
  if (prefColumn) columns.push(prefColumn);

  const { data, error } = await supabase
    .from('individual')
    .select(columns.join(','))
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    console.warn('[notificationService] Failed to load recipient context:', error.message);
    return null;
  }

  return data || null;
}

async function hasRecentDuplicateNotification(userId, type, title, link) {
  const windowStart = new Date(Date.now() - (1000 * 60 * 10)).toISOString();

  let query = supabase
    .from('notification')
    .select('id')
    .eq('user_id', userId)
    .eq('type', type)
    .eq('title', title)
    .eq('read', false)
    .gte('created_at', windowStart)
    .limit(1);

  query = link ? query.eq('link', link) : query.is('link', null);

  const { data, error } = await query.maybeSingle();

  if (error) {
    console.warn('[notificationService] Duplicate-check failed:', error.message);
    return false;
  }

  return !!data?.id;
}

/**
 * Create an in-app notification for a user.
 * Checks user preferences before inserting. Fire-and-forget — failures log but don't throw.
 *
 * @param {string} userId - The individual.id to notify
 * @param {string} type - One of the NOTIFICATION_CONFIG keys
 * @param {object} context - Event context (jobTitle, rate, amount, itemCount, etc.)
 * @param {string} [orgId] - Organization ID for multi-tenancy
 */
export async function createNotification(userId, type, context = {}, orgId = null) {
  try {
    if (!userId || !type) {
      console.warn('[notificationService] Missing userId or type:', { userId, type });
      return;
    }

    const config = NOTIFICATION_CONFIG[type];
    if (!config) {
      console.warn('[notificationService] Unknown notification type:', type);
      return;
    }

    const recipient = await resolveRecipientContext(userId, config.pref);
    if (!recipient) {
      console.warn('[notificationService] No recipient context found for:', userId);
      return;
    }

    if (config.pref && recipient[config.pref] === false) {
      return;
    }

    const title = config.title(context);
    const body = config.body ? config.body(context) : null;
    const link = config.link ? config.link(context) : null;
    const effectiveOrgId = orgId || recipient.org_id || null;

    if (orgId && recipient.org_id && orgId !== recipient.org_id) {
      console.warn('[notificationService] Org mismatch for notification:', { userId, orgId, recipientOrgId: recipient.org_id });
      return;
    }

    if (await hasRecentDuplicateNotification(userId, type, title, link)) {
      return;
    }

    const { error } = await supabase.from('notification').insert({
      user_id: userId,
      type,
      title,
      body,
      link,
      org_id: effectiveOrgId,
      read: false,
    });

    if (error) {
      console.warn('[notificationService] Failed to create notification:', error.message);
    }
  } catch (err) {
    console.warn('[notificationService] Unexpected error:', err.message);
  }
}

/**
 * Mark a single notification as read.
 * @param {string} notificationId
 */
export async function markAsRead(notificationId) {
  if (!notificationId) return;
  const { error } = await supabase
    .from('notification')
    .update({ read: true })
    .eq('id', notificationId);
  if (error) console.warn('[notificationService] markAsRead failed:', error.message);
}

/**
 * Mark all unread notifications as read for a user.
 * @param {string} userId
 */
export async function markAllAsRead(userId) {
  if (!userId) return;
  const { error } = await supabase
    .from('notification')
    .update({ read: true })
    .eq('user_id', userId)
    .eq('read', false);
  if (error) console.warn('[notificationService] markAllAsRead failed:', error.message);
}

/**
 * Get the icon for a notification type.
 * @param {string} type
 * @returns {string}
 */
export function getNotificationIcon(type) {
  return NOTIFICATION_CONFIG[type]?.icon || '🔔';
}

/**
 * Resolve a client.id to the corresponding individual.id via Postgres RPC.
 * Used to find which user to notify for client events (batch delivered, project completed).
 * @param {string} clientId - The client table UUID
 * @returns {string|null} - The individual.id or null
 */
export async function resolveClientIndividualId(clientId) {
  if (!clientId) return null;
  const { data, error } = await supabase.rpc('resolve_client_individual_id', {
    p_client_id: clientId,
  });
  if (error) {
    console.warn('[notificationService] resolveClientIndividualId failed:', error.message);
    return null;
  }
  return data || null;
}

export { NOTIFICATION_CONFIG };
